import { atom, useRecoilState, useResetRecoilState, useSetRecoilState } from "recoil";
import ss from '../../Data/Session';
import { useCallback } from "react";
import { logBehavior } from "../../Data/Behavior";
import { randomString } from "@/Functions";
import { localStorageDB } from "@/Data/LocalStorage";
import { userDatabase } from "@/Data/User/Database";
import { SessionWithProfile } from "ai-api/types/session";
import { useUsers } from "../User/User";

// Validation function for session data
const validateSession = (session: any): session is SessionWithProfile => {
    return session && 
           typeof session.userid === 'string' && 
           typeof session.addressid === 'string' && 
           typeof session.token === 'string' && 
           typeof session.email === 'string' && 
           typeof session.jwt === 'string' && 
           typeof session.memberState === 'string' && 
           typeof session.image === 'string' && 
           (typeof session.gender === 'string' || session.gender === null) && 
           (typeof session.birthday === 'string' || session.birthday === null) && 
           typeof session.name === 'string';
};

localStorageDB.init();
const localStorage = localStorageDB;

const localStorageEffect = (key: string) => ({setSelf, onSet}: {setSelf: Function, onSet: Function}) => {
    try {
        const savedValue = localStorage.getItem(key);
        if (savedValue) {
            const parsedSession = JSON.parse(savedValue);
            
            // Validate session before setting state
            if (validateSession(parsedSession)) {
                setSelf(parsedSession);
            } else {
                console.warn('Invalid session data in localStorage, using default session');
                // If invalid, don't set and let it use the default
            }
        }
    } catch (error) {
        console.error('Error loading session from localStorage:', error);
        // If error in parsing, let it use the default session
    }

    onSet((newSession: SessionWithProfile) => {
        if (validateSession(newSession)) {
            localStorage.setItem(key, JSON.stringify(newSession));
        } else {
            console.error('Attempted to save invalid session data to localStorage:', newSession);
        }
    });
};


const logBehaviorEffect = (what: any, detail: any) => ({onSet}: {onSet: Function}) => {
    onSet((newSession: SessionWithProfile) => {
        if (newSession.userid){
            logBehavior(what, detail);
        }
    })
}

const userSessionState = atom<SessionWithProfile>({
    key: 'userSessionState'+randomString(4),
    default: {
        userid: '',
        addressid: '',
        token: '',
        email: '',
        jwt: '',
        memberState: '',
        image: '',
        gender: '',
        birthday: '',
        name: ''
    },
    effects: [
        localStorageEffect('session'),
        logBehaviorEffect("login(success)", {})
    ]
});

export const useSession = () : {
    session: SessionWithProfile,
    sessionRefresh: () => Promise<SessionWithProfile|null>,
    waitSession: () => Promise<SessionWithProfile>
} => {
    const [session, setSession] = useRecoilState(userSessionState);

    const sessionRefresh = useCallback(async (): Promise<SessionWithProfile|null> => {
        try {
            const newSession = await ss.sessionRefresh();
            
            // Guard clause - validate session before updating state
            if (!newSession || !validateSession(newSession)) {
                console.error('Invalid session data received from sessionRefresh:', newSession);
                return null;
            }
            
            // Update session state with validated data
            setSession(newSession);
            
            // Return success based on whether user ID exists
            return newSession;
        } catch (error) {
            console.error('Error refreshing session:', error);
            return null;
        }
    }, [setSession]);

    const waitSession = useCallback(async (): Promise<SessionWithProfile> => {
        if (session.userid) {
            return session;
        }
        
        await sessionRefresh();

        return session;
    }, [session, sessionRefresh]);

    return {
        session,
        sessionRefresh,
        waitSession
    }
}

export const useSignIn = () => {
    const setSessionState = useSetRecoilState(userSessionState);

    // Helper function to validate and set session state
    const validateAndSetSession = useCallback((newSession: any) => {
        // Guard clause - validate session before updating state
        if (!newSession || !validateSession(newSession)) {
            console.error('Invalid session data received:', newSession);
            return false;
        }
        
        // Update session state with validated data
        setSessionState(newSession);
        
        // Return the validated session if email exists, otherwise false
        return newSession.email ? newSession : false;
    }, [setSessionState]);




    const EmailSignupWithOtp = useCallback(async (email: any, otp: string, name: string, userid?: string) => {
        try {
            // Guard clause - validate input data for either email or userid OTP
            if ((!email || typeof email !== 'string') && (!userid || typeof userid !== 'string')) {
                console.error('Either email or userid must be provided');
                return false;
            }
            if (!otp || !name) {
                console.error('Invalid OTP or name provided');
                return false;
            }
            
            const newSession = await ss.EmailSignupWithOtp(email, otp, name, userid);
            return validateAndSetSession(newSession);
        } catch (error) {
            console.error('Error during email signup with OTP:', error);
            return false;
        }
    }, [validateAndSetSession]);

    const users = useUsers();
    const resetSession = useResetRecoilState(userSessionState);

    const logout = useCallback(async () => {
        try {
            const newSession = await ss.logout();
            resetSession(); // Reset session state

            try {
                // Clear databases on logout
                await userDatabase.dropTables();
                users.reset();
                
                await localStorageDB.clear();
            } catch (error) {
                console.error('Failed to clear databases during logout:', error);
                // Continue with logout even if database clearing fails
            }

            // Update session state with empty session
            setSessionState(newSession);
            
            return true;
        } catch (error) {
            console.error('Error during logout:', error);
            return false;
        }
    }, [users, resetSession, setSessionState]);

    const requestOtp = useCallback(async (params: { email?: string; userid?: string; notice_by?: string }) => {
        try {
            const result = await ss.requestOtp(params);

            if (result.code === 200) {
                logBehavior('otp_requested', {
                    email: params.email,
                    userid: params.userid,
                    method: params.notice_by || 'email'
                });
            }

            return result;
        } catch (error) {
            console.error('OTP request error:', error);
            logBehavior('otp_request_error', {
                email: params.email,
                userid: params.userid,
                error: (error as Error).message
            });
            return { code: 500, message: (error as Error).message };
        }
    }, []);

    const createAuthRequest = useCallback(async () => {
        try {
            const result = await ss.createAuthRequest();
            
            if (!result.error) {
                logBehavior('cross_auth_request_created', {
                    pincode: result.pincode
                });
            }
            
            return result;
        } catch (error) {
            console.error('Create auth request error:', error);
            logBehavior('cross_auth_request_error', {
                error: (error as Error).message
            });
            return { error: (error as Error).message };
        }
    }, []);

    const pollAuthStatus = useCallback(async (pincode: string) => {
        try {
            const result = await ss.pollAuthStatus(pincode);

            if (result.success) {
                validateAndSetSession(result.session);
                logBehavior('cross_auth_success', {
                    pincode,
                    userid: result.session?.userid
                });
            }

            return result;
        } catch (error) {
            console.error('Poll auth status error:', error);
            return { error: (error as Error).message };
        }
    }, [validateAndSetSession]);

    const googleSignIn = useCallback(async (code: string) => {
        try {
            const newSession = await ss.GoogleSignin({ code });
            return validateAndSetSession(newSession);
        } catch (error) {
            console.error('Google sign-in error:', error);
            logBehavior('google_signin_error', { error: (error as Error).message });
            return false;
        }
    }, [validateAndSetSession]);

    return {
        EmailSignupWithOtp,
        requestOtp,
        createAuthRequest,
        pollAuthStatus,
        googleSignIn,
        logout
    };
}